<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useRegion } from "@/composables/useRegion";
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

const { locale } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const { currentRegion, setRegion, isSwiss } = useRegion();

const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const languages = [
  { code: "fr", label: "FR", name: "Français" },
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
];

const currencies = [
  { code: "CH", label: "CHF", name: "Franc Suisse (CHF)", flag: "🇨🇭" },
  { code: "FR", label: "EUR", name: "Euro (EUR)", flag: "🇪🇺" },
];

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const switchLanguage = (code: string) => {
  if (locale.value === code) return;
  const path = switchLocalePath(code as any);
  window.location.href = path; // Force reload to prevent hydration issues
  closeDropdown();
};

const selectRegion = (code: string) => {
  setRegion(code as "CH" | "FR");
  closeDropdown();
};

const handleEvent = (event: Event) => {
  if (
    isOpen.value &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node)
  ) {
    closeDropdown();
  }
};

const handleScroll = () => {
  if (isOpen.value) {
    closeDropdown();
  }
};

onMounted(() => {
  window.addEventListener("click", handleEvent);
  window.addEventListener("touchstart", handleEvent);
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("click", handleEvent);
  window.removeEventListener("touchstart", handleEvent);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <!-- Trigger Button -->
    <button
      @click="toggleDropdown"
      class="relative flex h-8 items-center justify-center gap-1.5 px-3 rounded-full border border-primary/30 text-primary font-semibold text-xs transition hover:border-primary hover:bg-primary/5"
      :aria-expanded="isOpen"
    >
      <span>{{ locale.toUpperCase() }}</span>
      <span class="w-px h-3 bg-primary/20"></span>
      <span class="text-sm leading-none flex items-center">{{
        isSwiss ? "🇨🇭" : "🇪🇺"
      }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-3 h-3 text-primary/60 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0 scale-95"
      enter-to-class="translate-y-0 opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100 scale-100"
      leave-to-class="translate-y-1 opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50 origin-top-right ring-1 ring-black ring-opacity-5"
      >
        <!-- Language Section -->
        <div class="p-3">
          <div
            class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2"
          >
            Langue
          </div>
          <div class="grid grid-cols-1 gap-1">
            <button
              v-for="lang in languages"
              :key="lang.code"
              @click="switchLanguage(lang.code)"
              class="flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors group"
              :class="
                locale === lang.code
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              "
            >
              <span class="flex items-center gap-2">
                {{ lang.name }}
              </span>
              <svg
                v-if="locale === lang.code"
                class="w-4 h-4 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="border-t border-gray-100 dark:border-gray-800"></div>

        <!-- Currency/Region Section -->
        <div class="p-3">
          <div
            class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2"
          >
            Devise
          </div>
          <div class="grid grid-cols-1 gap-1">
            <button
              v-for="currency in currencies"
              :key="currency.code"
              @click="selectRegion(currency.code)"
              class="flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors group"
              :class="
                (isSwiss && currency.code === 'CH') ||
                (!isSwiss && currency.code === 'FR')
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              "
            >
              <span class="flex items-center gap-2">
                <span class="text-base">{{ currency.flag }}</span>
                {{ currency.name }}
              </span>
              <svg
                v-if="
                  (isSwiss && currency.code === 'CH') ||
                  (!isSwiss && currency.code === 'FR')
                "
                class="w-4 h-4 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
/* No specific styles needed, Tailwind covers it */
</style>
